// Boot: pick the backend, sign in, load everything, stay live.
import './styles.css';
import type { Pm } from '@shared/index';
import { supabaseBackend } from './backend/supabase';
import type { Backend, ChangeEvent, Table } from './backend/types';
import { useBackend } from './actions';
import { defaultPm, removeRow, store, upsertRow } from './store';
import { registerView, render, renderSoon } from './render';
import { showTab } from './nav';
import { $, cssName } from './ui';
import { initOverview } from './views/overview';
import { initJobs } from './views/jobs';
import { initCalendar } from './views/calendar';
import { initTodo } from './views/todo';
import { initAdmin } from './views/admin';

const env = import.meta.env;

async function pickBackend(): Promise<Backend | null> {
  if (env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY) {
    return supabaseBackend(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, env.VITE_SYNC_URL || '');
  }
  if (import.meta.env.DEV) {
    // No Supabase configured: run on the local seed. ?role=pm|viewer&pm=Bobby tries other logins.
    const p = new URLSearchParams(location.search);
    const { memoryBackend } = await import('./backend/memory');
    store.demo = true;
    return memoryBackend((p.get('role') as 'admin' | 'pm' | 'viewer') || 'admin', p.get('pm'));
  }
  return null;
}

/** PM colours come from the pms table, for light and dark themes. */
function pmStyles(pms: Pm[]) {
  const light = pms.map((p) => `--pm-${cssName(p.name)}: ${p.colour_light};`).join(' ');
  const dark = pms.map((p) => `--pm-${cssName(p.name)}: ${p.colour_dark};`).join(' ');
  let tag = document.getElementById('pm-colours') as HTMLStyleElement | null;
  if (!tag) { tag = document.createElement('style'); tag.id = 'pm-colours'; document.head.append(tag); }
  tag.textContent = `:root { ${light} }
@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }
:root[data-theme="dark"] { ${dark} }`;
}

function chrome() {
  pmStyles(store.pms);
  const names = store.pms.filter((p) => p.active).map((p) => p.name);
  const list = names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : names.join('');
  $('subline').textContent = 'Money and progress on every open job for ' + list + '. Changes save straight away for everyone.';
  $('ro').hidden = store.canWrite;
  $('adminTab').hidden = store.profile?.role !== 'admin';
  $('demoNote').hidden = !store.demo;
  showTab();
}

function showLogin(backend: Backend, problem?: string) {
  $('view-login').hidden = false;
  $('app').hidden = true;
  // A link opened in another tab of this browser signs this tab in too; pick that up.
  backend.onSignedIn(() => location.reload());
  const form = $('loginForm') as HTMLFormElement;
  const msg = $('loginMsg');
  if (problem) { msg.className = 'msg bad'; msg.textContent = problem; }
  let sentTo = '';
  ($('codeForm') as HTMLFormElement).onsubmit = async (e) => {
    e.preventDefault();
    const code = ($('loginCode') as HTMLInputElement).value.replace(/\s+/g, '');
    if (!sentTo || !/^\d{6,10}$/.test(code)) { msg.className = 'msg bad'; msg.textContent = 'Enter the code from the email.'; return; }
    ($('codeBtn') as HTMLButtonElement).disabled = true;
    try {
      await backend.verifyCode(sentTo, code);
      location.reload();
    } catch {
      msg.className = 'msg bad';
      msg.textContent = 'That code didn’t work. It may have expired: request a new one.';
      ($('codeBtn') as HTMLButtonElement).disabled = false;
    }
  };
  form.onsubmit = async (e) => {
    e.preventDefault();
    const email = ($('loginEmail') as HTMLInputElement).value.trim().toLowerCase();
    msg.className = 'msg';
    if (!/@hangansteel\.com\.au$/.test(email)) {
      msg.className = 'msg bad'; msg.textContent = 'Use your @hangansteel.com.au email address.';
      return;
    }
    ($('loginBtn') as HTMLButtonElement).disabled = true;
    msg.textContent = 'Sending…';
    try {
      await backend.sendMagicLink(email);
      sentTo = email;
      msg.className = 'msg ok';
      msg.textContent = 'Check your email for a sign-in link and code.';
      $('codeForm').hidden = false;
    } catch (err) {
      msg.className = 'msg bad';
      msg.textContent = (err as Error).message || 'Couldn’t send the link. Try again.';
    } finally {
      ($('loginBtn') as HTMLButtonElement).disabled = false;
    }
  };
}

async function loadEverything(backend: Backend) {
  const d = await backend.loadAll();
  store.jobs = d.jobs; store.stages = d.stages; store.tasks = d.tasks; store.pms = d.pms;
  store.loaded = true;
}

/**
 * Arriving from an emailed link: ?token_hash=… (works in any browser) is exchanged here.
 * Supabase reports a failed link (expired, or opened in a different browser) as error_description.
 * Returns a message to show on the sign-in screen, if any.
 */
async function handleEmailLink(backend: Backend): Promise<string | undefined> {
  const q = new URLSearchParams(location.search);
  const h = new URLSearchParams(location.hash.replace(/^#/, ''));
  const failed = q.get('error_description') || h.get('error_description');
  const tokenHash = q.get('token_hash');
  const type = q.get('type') || 'email';
  if (!failed && !tokenHash) return undefined;
  history.replaceState(null, '', location.pathname); // never leave tokens in the address bar
  if (failed) return 'That sign-in link didn’t work (' + failed + '). Request a new one below.';
  try {
    await backend.verifyLink(tokenHash!, type);
    return undefined;
  } catch {
    return 'That sign-in link has expired or was already used. Request a new one below.';
  }
}

async function start(backend: Backend) {
  const problem = await handleEmailLink(backend);
  const email = await backend.session();
  if (!email) { showLogin(backend, problem); return; }

  const profile = await backend.profile();
  if (!profile) {
    $('view-login').hidden = false;
    $('loginMsg').className = 'msg bad';
    $('loginMsg').textContent = 'Your account isn’t set up yet. Ask Josh to check it.';
    return;
  }
  store.profile = profile;
  store.canWrite = profile.role === 'admin' || profile.role === 'pm';
  if (profile.pm_name) defaultPm(profile.pm_name);

  $('view-login').hidden = true;
  $('app').hidden = false;
  $('who').hidden = false;
  $('whoEmail').textContent = email;
  $('signOut').onclick = async () => { await backend.signOut(); location.hash = ''; location.reload(); };

  initOverview(); initJobs(); initCalendar(); initTodo(); initAdmin();
  registerView('chrome', chrome);
  chrome();

  try {
    await loadEverything(backend);
  } catch (e) {
    console.error(e);
    $('count').textContent = 'Couldn’t load jobs. Reload the page to try again.';
    $('lede').textContent = 'Couldn’t load jobs. Reload the page to try again.';
    return;
  }
  render();

  // Live: apply each change as it arrives. After a dropped connection, reload so nothing is missed.
  const KEY: Record<Table, string> = { jobs: 'quote', stages: 'id', tasks: 'id', pms: 'name' };
  backend.subscribe((table: Table, ev: ChangeEvent, row, old) => {
    if (ev === 'DELETE') { const k = old?.[KEY[table]]; if (k) removeRow(table, String(k)); }
    else if (row) upsertRow(table, row);
    renderSoon();
  }, async () => { await loadEverything(backend); renderSoon(); });

  // Phones sleep: catch up when the tab comes back after a while.
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', async () => {
    if (document.hidden) { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt > 60_000) { await loadEverything(backend).catch(() => {}); renderSoon(); }
  });
}

window.addEventListener('hashchange', showTab);

(async () => {
  const backend = await pickBackend();
  if (!backend) {
    $('view-login').hidden = false;
    $('loginForm').hidden = true;
    $('loginMsg').textContent = 'This copy of the tracker isn’t configured: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are missing.';
    return;
  }
  useBackend(backend);
  await start(backend);
})();
